import type React from 'react';
import { useMemo } from 'react';

import { useGetFace, useGetFaces } from '@vlabs/api/luna-api/client';
import type { FaceId } from '@vlabs/api/luna-api/model';
import { FaceCard } from '@vlabs/monokit/shared/ui/face-card';
import { AttachFaceToCardButton } from '@vlabs/monokit/widgets/attach-face-to-card-button';
import { useTranslation } from 'react-i18next';
import { generatePath, useHistory } from 'react-router-dom';

import st from './SameExternalIdFacesSection.module.sass';
import { routes } from '@vlapps/point/src/app/routes';
import { PageSection } from '../../page-section';

interface SameExternalIdFacesSectionProps {
  faceId: FaceId;
}

function SameExternalIdFacesSection({
  faceId,
}: SameExternalIdFacesSectionProps): React.ReactNode {
  const { t } = useTranslation();
  const history = useHistory();
  const getCurrentFace = useGetFace(faceId, { targets: ['external_id'] });
  // @ts-expect-error enabled
  const getFaces = useGetFaces({ external_ids: [getCurrentFace.data?.external_id], targets: ['face_id'] }, {
    query: {
      enabled: !!getCurrentFace.data?.external_id,
    },
  });

  const faces = useMemo(() => getFaces.data?.faces?.map((face) => {
    if (face.face_id === faceId) return undefined;
    return (
      <FaceCard
        can={() => true}
        faceId={face.face_id}
        key={face.face_id}
        size="small"
      />
    );
  }).filter((f) => f !== undefined) ?? [], [faceId, getFaces.data?.faces]);

  if (!getCurrentFace.isFetched) return null;

  return (
    <PageSection title={t('face-page:same_external_id_faces.title')}>
      {!!getCurrentFace.data?.external_id && faces.length > 0 && <div className={st.Faces}>{faces}</div>}
      {!!getCurrentFace.data?.external_id && faces.length === 0 && (t('face-page:same_external_id_faces.no_faces')) }
      {!getCurrentFace.data?.external_id && (
        <div className={st.NoFaces}>
          <AttachFaceToCardButton
            faceId={faceId}
            onAttachFaceToCard={({ fromState, cardId }) => {
              if (fromState === 'create') {
                history.push(generatePath(routes.externalId.read, { externalId: cardId }));
              }
            }}
          />
        </div>
      )}
    </PageSection>
  );
}

export { SameExternalIdFacesSection };
